import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaSchool, FaSearch, FaArrowRight, FaLock, FaGraduationCap, FaChalkboardTeacher, FaBookOpen, FaChartBar } from 'react-icons/fa';
import Spinner from '../../components/common/Spinner';

export default function PlatformHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [schools,  setSchools]  = useState([]);
  const [search,   setSearch]   = useState('');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (user) {
      if (user.type === 'platform' || user.role === 'super_admin') navigate('/super-admin');
      else if (user.role === 'admin')   navigate('/admin');
      else if (user.role === 'teacher') navigate('/teacher');
      else if (user.role === 'learner') navigate('/learner');
      else if (user.role === 'parent')  navigate('/parent');
    }
  }, [user, navigate]);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost/school_sms/api';
    fetch(`${apiBase}/school/list.php${search ? `?search=${encodeURIComponent(search)}` : ''}`)
      .then(r => r.json())
      .then(d => { if (d.success) setSchools(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search]);

  const features = [
    { icon: FaGraduationCap,      label: 'Learner Management',  desc: 'Enrol and track every learner',         color: '#6366f1' },
    { icon: FaChalkboardTeacher,  label: 'Teacher Portal',      desc: 'Attendance, marks & materials',         color: '#0ea5e9' },
    { icon: FaChartBar,           label: 'Results & Reports',   desc: 'Assessments and report cards',          color: '#10b981' },
    { icon: FaBookOpen,           label: 'Digital Library',     desc: 'Study materials & resources',           color: '#f59e0b' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0a0e1a', fontFamily: 'Inter, sans-serif', overflowX: 'hidden' }}>

      {/* ── TOP NAV ──────────────────────────────────────── */}
      <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 40px', position: 'sticky', top: 0, zIndex: 50, background: 'rgba(10,14,26,0.85)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, background: '#0d6efd', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FaSchool color="#fff" size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 800, color: '#fff', fontSize: '1rem', lineHeight: 1 }}>School SMS</div>
            <div style={{ color: '#64748b', fontSize: '0.68rem' }}>School Management System</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => navigate('/parent/login')}
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            👨‍👩‍👧 Parent Portal
          </button>
          <button onClick={() => navigate('/super-admin/login')}
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FaLock size={11} /> Admin
          </button>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────── */}
      <div style={{ position: 'relative', textAlign: 'center', padding: '80px 20px 60px', overflow: 'hidden' }}>
        {/* Background glow blobs */}
        <div style={{ position: 'absolute', top: -100, left: '20%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(13,110,253,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 50, right: '15%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(13,110,253,0.15)', border: '1px solid rgba(13,110,253,0.3)', borderRadius: 20, padding: '6px 16px', marginBottom: 24 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#0d6efd', animation: 'pulse 2s infinite' }} />
            <span style={{ color: '#60a5fa', fontSize: '0.8rem', fontWeight: 600 }}>Multi-School Management Platform</span>
          </div>

          <h1 style={{ color: '#fff', fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 900, lineHeight: 1.15, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
            Find Your School &<br />
            <span style={{ background: 'linear-gradient(135deg, #0d6efd, #6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Access Your Dashboard</span>
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '1.05rem', maxWidth: 520, margin: '0 auto 40px', lineHeight: 1.7 }}>
            The complete school management system for administrators, teachers, learners and parents.
          </p>

          {/* Search bar */}
          <div style={{ maxWidth: 520, margin: '0 auto', position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '0.95rem' }} />
            <input
              style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: '14px 18px 14px 46px', color: '#fff', fontSize: '1rem', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box' }}
              placeholder="Search for your school..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={e => e.target.style.borderColor = '#0d6efd'}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
            />
          </div>
        </div>
      </div>

      {/* ── SCHOOLS GRID ─────────────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px 60px' }}>
        {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}><Spinner color="#0d6efd" /></div> : (
          <>
            {schools.length > 0 && (
              <div style={{ color: '#64748b', fontSize: '0.82rem', marginBottom: 16, textAlign: 'center' }}>
                {schools.length} school{schools.length !== 1 ? 's' : ''} available
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 20 }}>
              {schools.map(s => (
                <SchoolCard key={s.school_id} school={s} onClick={() => navigate(`/school/${s.slug}/login`)} />
              ))}
            </div>
            {schools.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                <FaSchool size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
                <p style={{ fontSize: '1rem' }}>{search ? `No schools matching "${search}"` : 'No schools registered yet'}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── FEATURES ─────────────────────────────────────── */}
      <div style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.05)', padding: '60px 20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ color: '#fff', fontWeight: 800, fontSize: '1.8rem', margin: 0 }}>Everything a school needs</h2>
            <p style={{ color: '#64748b', marginTop: 8 }}>Built for admins, teachers, learners and parents</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16 }}>
            {features.map(f => (
              <div key={f.label} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '24px 20px', transition: 'background 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${f.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <f.icon size={20} color={f.color} />
                </div>
                <div style={{ fontWeight: 700, color: '#e2e8f0', marginBottom: 6 }}>{f.label}</div>
                <div style={{ color: '#64748b', fontSize: '0.83rem' }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FOOTER ───────────────────────────────────────── */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '24px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ color: '#475569', fontSize: '0.82rem' }}>© {new Date().getFullYear()} School SMS — All rights reserved</div>
        <div style={{ display: 'flex', gap: 16 }}>
          <button onClick={() => navigate('/parent/login')} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.82rem' }}>Parent Portal</button>
          <button onClick={() => navigate('/super-admin/login')} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.82rem' }}>Admin Login</button>
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
}

function SchoolCard({ school, onClick }) {
  const [hovered, setHovered] = useState(false);
  const primary = school.primary_color || '#0d6efd';

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${hovered ? primary + '66' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 16, padding: 24, cursor: 'pointer',
        transition: 'all 0.2s', position: 'relative', overflow: 'hidden',
        boxShadow: hovered ? `0 8px 32px ${primary}22` : 'none',
      }}>

      {/* Top accent bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${primary}, ${primary}88)`, opacity: hovered ? 1 : 0.4, transition: 'opacity 0.2s' }} />

      {/* School header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        {/* Logo */}
        <div style={{
          width: 60, height: 60, borderRadius: 14, flexShrink: 0, overflow: 'hidden',
          background: school.logo ? 'transparent' : `${primary}22`,
          border: `2px solid ${primary}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 4px 12px ${primary}33`,
        }}>
          {school.logo
            ? <img src={school.logo} alt={school.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <FaSchool color={primary} size={26} />
          }
        </div>

        <div style={{ overflow: 'hidden', flex: 1 }}>
          <div style={{ fontWeight: 800, color: '#f1f5f9', fontSize: '1rem', lineHeight: 1.3, textTransform: 'capitalize' }}>{school.name}</div>
          {school.principal_name && (
            <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: 2 }}>Principal: {school.principal_name}</div>
          )}
          {school.address && (
            <div style={{ color: '#64748b', fontSize: '0.73rem', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {school.address}</div>
          )}
        </div>
      </div>

      {/* Motto */}
      {school.motto && (
        <div style={{ background: `${primary}11`, border: `1px solid ${primary}22`, borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
          <p style={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic', margin: 0 }}>"{school.motto}"</p>
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <FaGraduationCap color="#64748b" size={12} />
          <span style={{ color: '#64748b', fontSize: '0.78rem' }}>{school.total_learners || 0} learners</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <FaChalkboardTeacher color="#64748b" size={12} />
          <span style={{ color: '#64748b', fontSize: '0.78rem' }}>{school.total_teachers || 0} teachers</span>
        </div>
      </div>

      {/* Login button */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: hovered ? primary : `${primary}22`,
        borderRadius: 10, padding: '10px 16px',
        transition: 'background 0.2s',
      }}>
        <span style={{ color: hovered ? '#fff' : primary, fontWeight: 700, fontSize: '0.88rem' }}>Login to Portal</span>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: hovered ? 'rgba(255,255,255,0.2)' : `${primary}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
          transform: hovered ? 'translateX(3px)' : 'none',
        }}>
          <FaArrowRight color={hovered ? '#fff' : primary} size={11} />
        </div>
      </div>
    </div>
  );
}
